import { parse, stringify } from 'qs';
import { isArray, mapKeys, camelCase, snakeCase, isEmpty } from 'lodash';
import {
  CeilingHeight,
  CeilingHeightMeasurementType,
  LeaseTypes,
  PriceRangeFilter,
  SearchCriteria,
  SearchCriteriaKeys,
  SpaceCondition,
} from '@root/types';
import routes from '@root/routes';
import { Possessions } from '../../Header/constants';

const toString = (collection: string[] | string | boolean | number[] | number): string =>
  isArray(collection) ? collection.join(',') : String(collection);
const camelizeKeys = obj => mapKeys(obj, (_v, key) => camelCase(key));
const snakeKeys = obj => mapKeys(obj, (_v, key) => snakeCase(key));
const sortArrayFilter = (filterValue: string) => filterValue.split(',').sort().join(',');

class ListingSearchCriteria {
  currentFilters: SearchCriteria;

  static fromUrl({ queryString, marketSlug = 'for-rent' }) {
    const filters = {
      ...camelizeKeys(
        parse(queryString, {
          ignoreQueryPrefix: true,
        }),
      ),
      ...(marketSlug === 'for-rent' ? {} : { markets: marketSlug }),
    } as SearchCriteria;

    return new ListingSearchCriteria(filters);
  }

  constructor(filters: SearchCriteria) {
    this.currentFilters = filters;
  }

  filters() {
    return this.currentFilters;
  }

  smartFilters() {
    return {
      spaceCondition: this.spaceConditions(),
      sizes: this.sizes(),
      terms: this.terms(),
      price: this.priceFilter(),
      ceilingHeight: this.ceilingHeightFilter(),
      possession: this.possession(),
      leaseTypes: this.leaseTypes(),
    };
  }

  numFilters() {
    const possessionNumber = this.possession() ? 1 : 0;
    const ceilingHeightNumber = this.ceilingHeightFilter().magnitude ? 1 : 0;
    const exclusiveNumber = this.exclusive() ? 1 : 0;
    const priceNumber =
      this.priceFilter().minPrice !== '' ||
      this.priceFilter().maxPrice !== '' ||
      !this.priceFilter().priceNegotiable
        ? 1
        : 0;
    const sizeNumber = this.sizes().minSize !== '' || this.sizes().maxSize !== '' ? 1 : 0;
    const keywordsNumber = isEmpty(this.keywords()) ? 0 : 1;

    return (
      this.spaceConditions().length +
      this.terms().length +
      this.leaseTypes().length +
      this.amenities().length +
      possessionNumber +
      ceilingHeightNumber +
      exclusiveNumber +
      sizeNumber +
      priceNumber +
      keywordsNumber
    );
  }

  isSaveable() {
    return this.numFilters() > 0 || this.isUsingMap() || this.neighborhoods().length > 0;
  }

  changeExclusivity(exclusivityValue: boolean) {
    // because exclusivity should not be passed
    // if false, we have to remove it
    if (exclusivityValue) {
      return this.add('exclusive', true);
    }

    return this.remove('exclusive');
  }

  add(key: SearchCriteriaKeys, values: string | string[] | boolean | number[] | number) {
    this.currentFilters = {
      ...this.currentFilters,
      [key]: toString(values),
    };
    if (key !== 'page') {
      this.remove('page');
    }
    return this.currentFilters;
  }

  addValue(key: SearchCriteriaKeys, value: string) {
    const currentFilterValue = this.currentFilters[key];
    this.currentFilters = {
      ...this.currentFilters,
      [key]: toString(
        currentFilterValue ? [...(currentFilterValue.split(',') || []), value] : [value],
      ),
    };
    this.remove('page');
  }

  remove(key: SearchCriteriaKeys) {
    const filterCopy: SearchCriteria = this.currentFilters;

    if (key.toString() === 'map') {
      delete filterCopy['custom-map'];
    }

    delete filterCopy[key];

    if (key !== 'page') {
      delete filterCopy.page;
    }

    this.currentFilters = filterCopy;

    return this.currentFilters;
  }

  removeValue(key: SearchCriteriaKeys, valueToDelete: string) {
    const valuesToFilter = this.currentFilters[key];

    if (valuesToFilter) {
      const newValues = toString(
        valuesToFilter.split(',').filter(currentValue => currentValue !== valueToDelete),
      );

      if (newValues.length) {
        this.currentFilters = {
          ...this.currentFilters,
          [key]: newValues,
        };
        this.remove('page');
        return this.currentFilters;
      }
      return this.remove(key);
    }
    return this.currentFilters;
  }

  replace(key: SearchCriteriaKeys, values: string | string[] | undefined | null) {
    if (values && values.length) {
      return this.add(key, values);
    }

    return this.remove(key);
  }

  removeAllFilters() {
    const newFilters = { sort: this.currentFilters.sort } as SearchCriteria;
    this.currentFilters = newFilters;

    return this.currentFilters;
  }

  addPolygons(polygons: string) {
    this.remove('markets');
    this.remove('map');
    this.add('polygons', polygons);
  }

  addMap(coordinates: string) {
    this.remove('markets');
    this.remove('polygons');
    this.add('map', coordinates);
  }

  changeMarkets(markets: string[]) {
    this.remove('map');
    this.remove('polygons');
    if (markets.length > 0) {
      this.add('markets', markets);
    } else {
      this.remove('markets');
    }
  }

  changeKeywords(keywords: string) {
    if (isEmpty(keywords)) {
      this.remove('keywords');
    } else {
      this.add('keywords', keywords);
    }

    return this.currentFilters;
  }

  changeCeilingHeight(ceilingHeight: CeilingHeight) {
    if (ceilingHeight.magnitude) {
      this.add('ceilingHeightType', ceilingHeight.type);
      this.add('ceilingHeightMagnitude', ceilingHeight.magnitude);
    } else {
      this.remove('ceilingHeightType');
      this.remove('ceilingHeightMagnitude');
    }
    return this.currentFilters;
  }

  changeSizeFilter(sizeFilter) {
    if (sizeFilter.minSize) {
      this.add('minSize', sizeFilter.minSize);
    } else {
      this.remove('minSize');
    }

    if (sizeFilter.maxSize) {
      this.add('maxSize', sizeFilter.maxSize);
    } else {
      this.remove('maxSize');
    }
  }

  changePriceFilter(priceFilter: PriceRangeFilter) {
    const hasPriceInput = priceFilter.minPrice.length > 0 || priceFilter.maxPrice.length > 0;
    if (priceFilter.minPrice) {
      this.add('minPrice', priceFilter.minPrice);
    } else {
      this.remove('minPrice');
    }

    if (priceFilter.maxPrice) {
      this.add('maxPrice', priceFilter.maxPrice);
    } else {
      this.remove('maxPrice');
    }

    if (priceFilter.priceNegotiable && hasPriceInput) {
      this.add('priceNegotiable', true);
    } else if (priceFilter.priceNegotiable === false && !hasPriceInput) {
      this.add('priceNegotiable', false);
    } else {
      this.remove('priceNegotiable');
    }
    return this.currentFilters;
  }

  possession() {
    if (!this.currentFilters.possession) return null;
    return this.currentFilters.possession as Possessions;
  }

  sizes() {
    const { minSize, maxSize } = this.filters();
    return { minSize: minSize || '', maxSize: maxSize || '' };
  }

  spaceConditions() {
    return (this.currentFilters.spaceCondition?.split(',') || []) as SpaceCondition[];
  }

  terms() {
    return this.currentFilters.terms?.split(',') || [];
  }

  exclusive() {
    return this.currentFilters.exclusive === 'true';
  }

  ceilingHeightFilter(): CeilingHeight {
    const emptyCeilingHeightType = 'finished';
    const { ceilingHeightType, ceilingHeightMagnitude } = this.currentFilters;

    return {
      type: (ceilingHeightType as CeilingHeightMeasurementType) || emptyCeilingHeightType,
      magnitude: ceilingHeightMagnitude ? Number(ceilingHeightMagnitude) : null,
    };
  }

  sort() {
    return this.currentFilters.sort || 'newest';
  }

  page() {
    return this.currentFilters.page ? Number(this.currentFilters.page) : 1;
  }

  priceFilter(): PriceRangeFilter {
    const { minPrice, maxPrice, priceNegotiable } = this.filters();
    const priceFilterInputSet = !!minPrice || !!maxPrice;
    return {
      minPrice: minPrice || '',
      maxPrice: maxPrice || '',
      priceNegotiable: priceFilterInputSet
        ? priceNegotiable === 'true'
        : !(priceNegotiable === 'false'),
    };
  }

  keywords() {
    return this.currentFilters.keywords ?? '';
  }

  amenities() {
    return this.currentFilters.amenities?.split(',') ?? [];
  }

  leaseTypes() {
    return (this.currentFilters.leaseTypes?.split(',') ?? []) as LeaseTypes[];
  }

  neighborhoods() {
    return this.currentFilters.markets?.split(',') ?? [];
  }

  mapCoordinates() {
    if (!this.isUsingMap()) {
      return null;
    }

    if (this.currentFilters.map) {
      return this.currentFilters.map;
    }

    return null;
  }

  isUsingMap() {
    return (
      Object.keys(this.currentFilters).includes('map') ||
      Object.keys(this.currentFilters).includes('polygons')
    );
  }

  toSavedSearchParameters() {
    const parameters: SearchCriteria = {
      ...this.currentFilters,
    };
    delete parameters.sort;
    delete parameters.page;
    delete parameters.markets;
    if (this.neighborhoods().length > 0) {
      parameters.markets = this.neighborhoods().sort().join(',');
    }
    if (parameters.spaceCondition) {
      parameters.spaceCondition = sortArrayFilter(parameters.spaceCondition);
    }

    if (parameters.terms) {
      parameters.terms = sortArrayFilter(parameters.terms);
    }

    if (parameters.leaseTypes) {
      parameters.leaseTypes = sortArrayFilter(parameters.leaseTypes);
    }

    return parameters;
  }

  toUrl() {
    const filters = snakeKeys({ ...this.currentFilters });
    let marketSlug = 'for-rent';

    if (this.neighborhoods().length === 1) {
      [marketSlug] = this.neighborhoods();
      delete filters.markets;
    }

    const base = `/office-space/${marketSlug}`;

    return `${base}${stringify(filters, { addQueryPrefix: true })}`;
  }

  toApiListingsUrl() {
    return `${routes.api.listings}${stringify(
      {
        ...this.currentFilters,
        markets: this.neighborhoods().length > 0 ? this.neighborhoods() : null,
      },
      {
        addQueryPrefix: true,
        skipNulls: true,
        encode: false,
        arrayFormat: 'comma',
      },
    )}`;
  }

  toApiBuildingsUrl() {
    const filtersForBuildings: Partial<Record<SearchCriteriaKeys, string | string[] | undefined>> =
      {
        ...this.currentFilters,
      };
    delete filtersForBuildings.sort;
    delete filtersForBuildings.page;
    delete filtersForBuildings.markets;
    if (this.neighborhoods().length > 0) {
      filtersForBuildings.markets = this.neighborhoods();
    }

    return `${routes.api.buildings}${stringify(filtersForBuildings, {
      addQueryPrefix: true,
      skipNulls: true,
      encode: false,
      arrayFormat: 'comma',
    })}`;
  }

  toApiMapMarkerUrl(buildingSlug: string) {
    // TODO : Combine this with the stuff on top to make it one function
    const filtersForBuildings: Partial<Record<SearchCriteriaKeys, string | string[] | undefined>> =
      {
        ...this.currentFilters,
      };
    delete filtersForBuildings.sort;
    delete filtersForBuildings.page;
    delete filtersForBuildings.markets;
    if (this.neighborhoods().length > 0) {
      filtersForBuildings.markets = this.neighborhoods();
    }

    return `${routes.api.buildingMapMarkers(buildingSlug)}${stringify(filtersForBuildings, {
      addQueryPrefix: true,
      skipNulls: true,
      encode: false,
      arrayFormat: 'comma',
    })}`;
  }
}

export default ListingSearchCriteria;
